import { Directive, inject, TemplateRef, ViewContainerRef, AfterViewInit, OnDestroy } from '@angular/core';
import { PageActionsService } from './page-actions.service';

/**
 * Directive to declare page-level action buttons from routed components.
 *
 * These actions are rendered in the layout's page header bar, aligned with breadcrumbs.
 * The actions are automatically registered when the component mounts and cleared when
 * it unmounts.
 *
 * @example
 * ```html
 * <ng-template zagPageActions>
 *   <button zagButton type="button" (click)="onCreate()">
 *     <span zagIcon="add"></span>
 *     {{ 'action.create' | translate }}
 *   </button>
 *   <button zagButton="secondary" type="button" (click)="onExport()">
 *     <span zagIcon="download"></span>
 *     {{ 'action.export' | translate }}
 *   </button>
 * </ng-template>
 * ```
 *
 * @example
 * ```typescript
 * // In your component
 * import { PageActionsDirective } from '@zag/ui';
 *
 * @Component({
 *   imports: [PageActionsDirective],
 *   template: `
 *     <ng-template zagPageActions>
 *       <button zagButton (click)="save()">Save</button>
 *     </ng-template>
 *     <!-- rest of template -->
 *   `
 * })
 * export class MyComponent {}
 * ```
 *
 * Note: Only one component's actions can be active at a time. If multiple components
 * try to register actions, the last one wins. This is the expected behavior for
 * nested routes where the deepest route should control the actions.
 */
@Directive({
  selector: '[zagPageActions]',
})
export class PageActionsDirective implements AfterViewInit, OnDestroy {
  private readonly pageActionsService = inject(PageActionsService);
  private readonly templateRef = inject(TemplateRef<void>);
  private readonly viewContainerRef = inject(ViewContainerRef);

  ngAfterViewInit(): void {
    this.pageActionsService.attach(this.templateRef, this.viewContainerRef);
  }

  ngOnDestroy(): void {
    this.pageActionsService.detach();
  }
}
